<template>
	<label class="px-2 flex flex-col w-full">
		<slot>
			<div class="pb-2">
				<span class="font-bold">{{ label }}</span>
				<small v-if="labelDescription">{{ labelDescription }}</small>
			</div>
		</slot>
		<div class="relative">
			<select
                v-model="value"
				v-bind="$attrs"
				v-on="inputListeners"
				class="input-text w-full appearance-none"
				:class="{
					'border-red-700': showRequiredMessage || showPatternMessage
				}"
				v-if="type === 'select'"
			>
				<option value=""></option>
				<option
					v-for="v in values"
					:key="v.Key"
					:value="v.Key"
					:selected="v.Key.toString() == value.toString()"
					>{{ v.Value }}</option
				>
			</select>

			<select
                v-model="value"
				v-bind="$attrs"
				v-on="inputListeners"
				class="input-text w-full appearance-none"
				:class="{
					'border-red-700': showRequiredMessage || showPatternMessage
				}"
				v-else-if="type === 'select-string'"
			>
				<option
					v-for="v in values"
					:key="v.Key"
					:value="v.Key"
					:selected="v.Key == value"
					>{{ v.Value }}</option
				>
			</select>

			<input
				type="text"
				inputmode="decimal"
				v-else-if="type === 'date'"
				v-bind="$attrs"
				v-on="inputListeners"
				class="input-text w-full"
				:class="{
					'border-red-700': showRequiredMessage || showPatternMessage
				}"
				:value="value"
				:required="required"
				v-mask="'##/##/####'"
				:placeholder="placeholder"
				pattern="^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$"
				:title="placeholder"
			/>

			<input
				:type="type"
				v-bind="$attrs"
				v-on="inputListeners"
				class="input-text w-full"
				:class="{
					'border-red-700': showRequiredMessage || showPatternMessage
				}"
				:value="value"
				:required="required"
				:pattern="pattern"
				:placeholder="placeholder"
				v-else
			/>

			<div
				v-if="type && type.indexOf('select') == 0"
				tabindex="-1"
				class="absolute pointer-events-none py-3 px-4 m-1 flex items-center pr-2 text-gray-900 bg-white"
				style="top:0px;right:0px;"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					class="w-4 h-4"
					stroke="currentColor"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M8 9l4-4 4 4m0 6l-4 4-4-4"
					/>
				</svg>
			</div>
			<div
				v-if="showValid"
				tabindex="-1"
				class="absolute pointer-events-none py-3 px-4 flex items-center"
				:class="{
					'pr-8': type && type.indexOf('select') == 0,
					'pr-4': !type || type.indexOf('select') == -1
				}"
				style="top:0px;right:0px;"
			>
				<svg
					width="20"
					height="20"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<rect width="20" height="20" rx="10" fill="#4E9036" />
					<path
						fill-rule="evenodd"
						clip-rule="evenodd"
						d="M8.472 12.195L14.667 6l.943.943-7.138 7.138-3.805-3.805.943-.943 2.862 2.862z"
						fill="#fff"
					/>
				</svg>
			</div>
			<div class="font-normal text-micro sm:text-xs">
				{{ patternMessage }}
			</div>

			<transition
				enter-active-class="transition-all duration-3s timing-ease"
				leave-active-class="transition-all duration-3s timing-ease"
				enter-class="opacity-0 scale-y-0 -translate-y-full"
				enter-to-class="opacity-1 scale-y-100 translate-y-0"
				leave-class="opacity-1 scale-y-100 translate-y-0"
				leave-to-class="opacity-0 scale-y-0 -translate-y-full"
			>
				<div v-if="showRequiredMessage || showPatternMessage">
					<div
						v-if="showRequiredMessage"
						class="bg-red-700 inline-block mt-2 text-white p-1 rounded-sm font-normal text-micro sm:text-xs"
					>
						Du skal udfylde dette felt
					</div>
					<div
						v-if="showPatternMessage"
						class="bg-red-700 inline-block mt-2 text-white p-1 rounded-sm font-normal text-micro sm:text-xs"
					>
						{{ patternErrorMessage }}
					</div>
				</div>
			</transition>
		</div>
	</label>
</template>

<script>
import { mask } from "vue-the-mask";
export default {
	inheritAttrs: false,
	props: {
		labelDescription: String,
		value: [String, Number, Date],
		values: Array,
		type: String,
		label: String,
		required: Boolean,
		pattern: String,
		patternMessage: String,
		patternErrorMessage: String,
		showRequiredMessage: Boolean,
		showPatternMessage: Boolean,
		showValid: Boolean,
		placeholder: String
	},
	directives: {
		mask
	},
	computed: {
		inputListeners: function() {
			var vm = this;
			return Object.assign({}, this.$listeners, {
				input: function(event) {
					var value = event.target.value;
					if (vm.type === "select-string" || vm.type === "select") {
						var selectedOption =
							event.target.options[event.target.selectedIndex];
						value = selectedOption.value || selectedOption.text;
						selectedOption.selected = true;
						if (vm.values)
						{
							var realValue = vm.values.filter(v => v.Key.toString() == value.toString());
							if (realValue.length > 0) {
								value = realValue[0].Key;
							}
						}
					}
					vm.$emit("input", value);
				},
				change: function(event) {
					var value = event.target.value;
					if (vm.type === "select-string" || vm.type === "select") {
						var selectedOption =
							event.target.options[event.target.selectedIndex];
						value = selectedOption.value || selectedOption.text || value;
						selectedOption.selected = true;
						if (vm.values)
						{
							var realValue = vm.values.filter(v => v.Key.toString() == value.toString());
							if (realValue.length > 0) {
								value = realValue[0].Key;
							}
						}
					}

					vm.$emit("change", value);
				}
			});
		}
	}
};
</script>
